<template>
  <div class="grid grid-cols-6 items-start gap-6">
    <div class="col-span-3">
      <label class="mb-1 block text-sm font-medium text-gray-700">{{
        t('archived')
      }}</label>
      <Toggle
        srLabel="Archived"
        :checked="siteSubscriberStore.siteSubscriber.archived"
        :onToggle="onToggleArchived"
      />
    </div>
    <div class="col-span-6">
      <div class="flex flex-col gap-y-2">
        <label for="sites" class="text-sm font-medium text-gray-700">{{
          t('jobAlerts.search.site') + '*'
        }}</label>

        <Multiselect
          id="sites"
          ref="multiselect"
          v-model="site"
          :options="searchSite"
          :filterResults="false"
          :noOptionsText="t('vacancy.company.noOptions')"
          :placeholder="t('jobAlerts.search.sitePlaceholder')"
          :resolveOnLoad="true"
          :minChars="0"
          :delay="500"
          :searchable="true"
          :object="true"
          valueProp="id"
          label="name"
          trackBy="name"
          inputType="search"
          autocomplete="off"
          class="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        >
          <template v-if="v$.site.$error" #caret>
            <div
              class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
            >
              <ExclamationCircleIcon
                class="h-5 w-5 text-red-500"
                aria-hidden="true"
              />
            </div>
          </template>
        </Multiselect>
        <div v-if="v$.site.$errors">
          <p
            v-for="(error, index) in v$.site.$errors"
            id="site-error"
            :key="index"
            class="text-sm text-red-600"
          >
            {{ error.$message }}
          </p>
        </div>
      </div>
    </div>
    <div class="col-span-6">
      <FormInput
        id="subscriberName"
        type="text"
        :value="siteSubscriberStore.siteSubscriber.name"
        :label="t('site.name') + '*'"
        :onInput="updateName"
        :rules="nameRules"
      />
    </div>
    <div class="col-span-6">
      <FormInput
        id="subscriberToken"
        type="text"
        :value="siteSubscriberStore.siteSubscriber.accessToken"
        :label="t('site.token') + '*'"
        :onInput="updateToken"
        :disabled="true"
        :rules="{}"
        :placeholder="
          siteSubscriberStore.siteSubscriber.id === 'new'
            ? t('siteSubscribers.tokenPlaceholder')
            : ''
        "
        inputClass="disabled:bg-gray-100"
      />
    </div>
    <div class="col-span-6">
      <FormInput
        id="subscriberEmail"
        type="text"
        :value="siteSubscriberStore.siteSubscriber.emailDomain"
        :label="t('siteSubscribers.emailDomain')"
        :onInput="updateEmailDomain"
        :rules="{}"
      />
    </div>
    <div class="col-span-6">
      <FormTextarea
        id="subscriberDescription"
        :value="siteSubscriberStore.siteSubscriber.description"
        :label="t('siteSubscribers.description')"
        :onInput="updateDescription"
        :rules="{}"
      />
    </div>
    <div class="col-span-2">
      <label class="mb-1 block text-sm font-medium text-gray-700">{{
        t('siteSubscribers.showExternal')
      }}</label>
      <Toggle
        :srLabel="t('siteSubscribers.showExternal')"
        :checked="siteSubscriberStore.siteSubscriber.showExternal"
        :onToggle="() => onTogglePublications('showExternal')"
      />
    </div>
    <div class="col-span-2">
      <label class="mb-1 block text-sm font-medium text-gray-700">{{
        t('siteSubscribers.showRegional')
      }}</label>
      <Toggle
        :srLabel="t('siteSubscribers.showRegional')"
        :checked="siteSubscriberStore.siteSubscriber.showRegional"
        :onToggle="() => onTogglePublications('showRegional')"
      />
    </div>
    <div class="col-span-2">
      <label class="mb-1 block text-sm font-medium text-gray-700">{{
        t('siteSubscribers.showInternal')
      }}</label>
      <Toggle
        :srLabel="t('siteSubscribers.showInternal')"
        :checked="siteSubscriberStore.siteSubscriber.showInternal"
        :onToggle="() => onTogglePublications('showInternal')"
      />
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { required, helpers } from '@vuelidate/validators'
import FormInput from '../FormInput.vue'
import Toggle from '../Toggle.vue'
import { computed } from 'vue'
import { useSiteSubscriberStore } from '../../store/siteSubscriberStore'
import Multiselect from '@vueform/multiselect'
import FormTextarea from '../FormTextarea.vue'
import tooManagement from '../../api/tooManagement'
import useVuelidate from '@vuelidate/core'
import { ExclamationCircleIcon } from '@heroicons/vue/20/solid'

const siteSubscriberStore = useSiteSubscriberStore()

const { t } = useI18n({
  inheritLocale: true,
  useScope: 'global',
})

const site = computed({
  get() {
    return siteSubscriberStore.siteSubscriber?.site
  },
  set(newSite) {
    siteSubscriberStore.siteSubscriber.site = newSite
  },
})
const siteRules = computed(() => ({
  site: {
    required: helpers.withMessage(t('validations.site.required'), required),
    $autoDirty: true,
  },
}))
const v$ = useVuelidate(siteRules, {
  site,
})

const nameRules = {
  required: helpers.withMessage(
    t('validations.subscriberName.required'),
    required,
  ),
  $autoDirty: true,
}
const updateName = (newName) => {
  siteSubscriberStore.siteSubscriber.name = newName
}

const onToggleArchived = () => {
  siteSubscriberStore.siteSubscriber.archived =
    !siteSubscriberStore.siteSubscriber.archived
}

const onTogglePublications = (fieldName) => {
  siteSubscriberStore.siteSubscriber[fieldName] =
    !siteSubscriberStore.siteSubscriber[fieldName]
}

const updateToken = (value) => {
  siteSubscriberStore.siteSubscriber.accessToken = value
}

const updateEmailDomain = (value) => {
  siteSubscriberStore.siteSubscriber.emailDomain = value
}

const updateDescription = (value) => {
  siteSubscriberStore.siteSubscriber.description = value
}

const searchSite = async (filter = '') => {
  const queryRes = await tooManagement.getSites({
    filter: filter || '',
    size: 15,
  })
  return queryRes.data
}
</script>
